import { motion } from "framer-motion";
import { ChevronDownIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { Subheading } from "src/components/base/heading";
import { Text } from "src/components/base/text";
import React from "react";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";

/**
 * The properties for {@link SimpleErrorBox}
 */
export type SimpleErrorBoxProps = {
    /** Error title */
    heading: string;
    /** Error description */
    description?: string;
    /** Data affected */
    data: Array<string>;
};

/**
 * Red Error Box with an unordered list of the data
 */
export function SimpleErrorBox(props: SimpleErrorBoxProps) {
    const { heading, description, data } = props;
    const [errOpen, setErrOpen] = React.useState<boolean>(false);

    return (
        <motion.div
            layout
            transition={{
                duration: 0.3,
            }}
            onClick={() => {
                setErrOpen(!errOpen);
            }}
            className={"rounded border border-red-500 bg-red-500/10 p-3 dark:border-red-800"}
        >
            <motion.div layout className={"flex justify-between gap-3"}>
                <div className={"flex gap-3"}>
                    <div className={"w-5 *:fill-red-500/80"}>
                        <ExclamationCircleIcon />
                    </div>
                    <Subheading>{heading}</Subheading>
                </div>
                <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                    <ChevronDownIcon />
                </div>
            </motion.div>
            <motion.div layout>
                <div className={errOpen ? "" : "hidden"}>
                    {description && <Text>{description}</Text>}

                    <ul className={"ml-4 list-disc text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400"}>
                        {data.map((entry) => (
                            <li>{entry}</li>
                        ))}
                    </ul>
                </div>
            </motion.div>
        </motion.div>
    );
}

/**
 * The properties for {@link ListErrorBox}
 */
export type ListErrorBoxProps = {
    /** Error title */
    heading: string;
    /** Error description */
    description?: string;
    /** Data affected */
    data: { [key: string]: string | undefined };
};

/**
 * Red Error Box with a Description List of the data
 */
export function ListErrorBox(props: ListErrorBoxProps) {
    const { heading, description, data } = props;
    const [errOpen, setErrOpen] = React.useState<boolean>(false);

    return (
        <motion.div
            layout
            transition={{
                duration: 0.3,
            }}
            onClick={() => {
                setErrOpen(!errOpen);
            }}
            className={"rounded border border-red-500 bg-red-500/10 p-3 dark:border-red-800"}
        >
            <motion.div layout className={"flex justify-between gap-3"}>
                <div className={"flex gap-3"}>
                    <div className={"w-5 *:fill-red-500/80"}>
                        <ExclamationCircleIcon />
                    </div>
                    <Subheading>{heading}</Subheading>
                </div>
                <div className={"w-5 *:fill-black/40 *:dark:fill-white/40"}>
                    <ChevronDownIcon />
                </div>
            </motion.div>
            <motion.div layout>
                <div className={errOpen ? "" : "hidden"}>
                    {description && <Text>{description}</Text>}

                    <DescriptionList>
                        {Object.entries(data).map(([key, value]) => (
                            <>
                                <DescriptionTerm>{key}</DescriptionTerm>
                                <DescriptionDetails>{value}</DescriptionDetails>
                            </>
                        ))}
                    </DescriptionList>
                </div>
            </motion.div>
        </motion.div>
    );
}
